import {
    Autocomplete,
    Box,
    Button,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
    EXPERIMENT_STATUS,
    PLOTMAP_STATUS,
    SORTED_COUNTRIES,
} from "./constants";
import { useFetch, useIsBasicRole, useSnackbar, useUsers } from "../../hooks";

import { BACKEND_ROUTES } from "../../backendRoutes";
import { CountryFlag } from "../../components/CountryFlag";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import { INPUT_VALIDATION } from "../../constants";
import { LoadingButton } from "@mui/lab";
import { NavigationBreadcrumbs } from "../../components";
import { SiteEditFormSkeleton } from "./SiteEditFormSkeleton";
import { styled } from "@mui/material/styles";
import { useSite } from "./useSite";

const LabelStyle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontWeight: 600,
}));

export const SiteEditForm = () => {
    const { patch } = useFetch();
    const isBasicRole = useIsBasicRole();
    const { openSnackbar } = useSnackbar();

    const {
        data: site,
        error: siteError,
        isLoading: isSiteLoading,
        mutate,
    } = useSite();

    const {
        data: users,
        error: usersError,
        isLoading: areUsersLoading,
    } = useUsers();

    const {
        control,
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({
        defaultValues: {
            name: "",
            displayName: "",
            contractUuid: "",
            processingLeadUuid: "",
            countryCode: null,
            plotmapStatus: 0,
            experimentStatus: -1,
        },
        values: site && {
            name: site.name,
            displayName: site.displayName,
            contractUuid: site.contractUuid,
            processingLeadUuid: site.processingLeadUuid,
            countryCode: site.countryCode
                ? (SORTED_COUNTRIES.find(
                      (country) => country.alpha3 === site.countryCode
                  ) ?? null)
                : null,
            plotmapStatus: Number(site.plotmapStatus),
            experimentStatus: Number(site.experimentStatus),
        },
    });

    const mergedFetchError = siteError ?? usersError;
    // TODO: `siteError` is displayed here and in MissionTable. The error should only be displayed once
    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;
    if ((!site && isSiteLoading) || (!users && areUsersLoading))
        return <SiteEditFormSkeleton />;

    const [experimentStatusWatch, plotmapStatusWatch] = watch([
        "experimentStatus",
        "plotmapStatus",
    ]);

    const onSubmit = async (payload) => {
        const updatedSite = await patch(`${BACKEND_ROUTES.SITE}/${site.uuid}`, {
            body: {
                ...payload,
                countryCode: payload.countryCode?.alpha3 ?? null,
            },
        });

        if (updatedSite) {
            openSnackbar(
                `Site ${updatedSite.displayName} updated successfully.`,
                "success"
            );

            mutate();
        }
    };

    const onCancel = () => {
        reset();
    };

    return (
        <>
            <NavigationBreadcrumbs
                company={site.Contract.Company}
                contract={site.Contract}
                site={site}
            />
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={1} alignItems="center">
                    <Grid size={12}>
                        <Typography variant="h5" gutterBottom>
                            Site management
                        </Typography>
                    </Grid>
                    <Grid size={2}>
                        <LabelStyle>Campaign</LabelStyle>
                    </Grid>
                    <Grid size={4}>
                        <Typography>{site.Contract.name}</Typography>
                    </Grid>
                    <Grid size={6} />
                    {!isBasicRole && (
                        <>
                            <Grid size={2}>
                                <LabelStyle>Name</LabelStyle>
                            </Grid>
                            <Grid size={4}>
                                <TextField
                                    name="name"
                                    id="name"
                                    disabled
                                    fullWidth
                                    type="text"
                                    error={Boolean(errors.name)}
                                    helperText={errors.name?.message}
                                    {...register("name", {
                                        required: INPUT_VALIDATION.REQUIRED,
                                    })}
                                />
                            </Grid>
                            <Grid size={6} />
                        </>
                    )}
                    <Grid size={2}>
                        <LabelStyle>
                            {isBasicRole ? "Name" : "Display Name"}{" "}
                        </LabelStyle>
                    </Grid>
                    <Grid size={4}>
                        <TextField
                            name="displayName"
                            id="displayName"
                            fullWidth
                            type="text"
                            error={Boolean(errors.displayName)}
                            helperText={errors.displayName?.message}
                            {...register("displayName", {
                                required: INPUT_VALIDATION.REQUIRED,
                            })}
                        />
                    </Grid>
                    <Grid size={6} />
                    {!isBasicRole && (
                        <>
                            <Grid size={2}>
                                <LabelStyle>Site Processing Lead</LabelStyle>
                            </Grid>
                            <Grid size={4}>
                                <Controller
                                    control={control}
                                    name="processingLeadUuid"
                                    render={({
                                        field: { ref, ...fieldProps },
                                    }) => (
                                        <TextField
                                            {...fieldProps}
                                            fullWidth
                                            id="processingLeadUuid"
                                            select
                                            inputRef={ref}
                                            error={Boolean(
                                                errors.processingLeadUuid
                                            )}
                                            helperText={
                                                errors.processingLeadUuid
                                                    ?.message
                                            }
                                        >
                                            {users ? (
                                                users.map((user) => (
                                                    <MenuItem
                                                        key={user.uuid}
                                                        value={user.uuid}
                                                    >
                                                        {user.firstName}{" "}
                                                        {user.lastName}
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem
                                                    key={
                                                        site.processingLeadUuid
                                                    }
                                                    value={
                                                        site.processingLeadUuid
                                                    }
                                                >
                                                    {
                                                        site.ProcessingLead
                                                            .firstName
                                                    }{" "}
                                                    {
                                                        site.ProcessingLead
                                                            .lastName
                                                    }
                                                </MenuItem>
                                            )}
                                        </TextField>
                                    )}
                                    rules={{
                                        required: INPUT_VALIDATION.REQUIRED,
                                    }}
                                />
                            </Grid>
                            <Grid size={6} />
                        </>
                    )}
                    <Grid size={2}>
                        <LabelStyle>Country</LabelStyle>
                    </Grid>
                    <Grid size={4}>
                        <Controller
                            control={control}
                            name="countryCode"
                            render={({
                                field: { ref, onChange, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="countryCode"
                                    disabled={isBasicRole}
                                    options={SORTED_COUNTRIES}
                                    isOptionEqualToValue={(option, value) =>
                                        option.alpha3 === value.alpha3
                                    }
                                    fullWidth
                                    onChange={(_, value) => {
                                        onChange(value);
                                    }}
                                    getOptionLabel={(option) => option.name}
                                    renderOption={(props, option) => (
                                        <Box {...props} component="li">
                                            <Stack direction="row" spacing={1}>
                                                <CountryFlag country={option} />
                                                <span>{option.name}</span>
                                            </Stack>
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            label="Country"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={6} />
                    {!isBasicRole && (
                        <>
                            <Grid size={2}>
                                <LabelStyle>Field map Status</LabelStyle>
                            </Grid>
                            <Grid size={4}>
                                <Controller
                                    control={control}
                                    name="experimentStatus"
                                    render={({
                                        field: { ref, ...fieldProps },
                                    }) => (
                                        <TextField
                                            {...fieldProps}
                                            disabled={plotmapStatusWatch > 0} // disabled if plot map is waiting for action
                                            fullWidth
                                            id="experimentStatus"
                                            select
                                            inputRef={ref}
                                            error={Boolean(
                                                errors.experimentStatus
                                            )}
                                            helperText={
                                                errors.experimentStatus?.message
                                            }
                                        >
                                            {Object.entries(
                                                EXPERIMENT_STATUS
                                            ).map(([key, { label }]) => (
                                                <MenuItem
                                                    key={key}
                                                    value={Number(key)}
                                                >
                                                    {label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                    rules={{
                                        required: INPUT_VALIDATION.REQUIRED,
                                    }}
                                />
                            </Grid>
                            <Grid size={6} />
                            <Grid size={2}>
                                <LabelStyle>Plot map Status</LabelStyle>
                            </Grid>
                            <Grid size={4}>
                                <Controller
                                    control={control}
                                    name="plotmapStatus"
                                    render={({
                                        field: { ref, ...fieldProps },
                                    }) => (
                                        <TextField
                                            {...fieldProps}
                                            disabled={experimentStatusWatch < 2} // disabled if field map is not completed
                                            fullWidth
                                            id="plotmapStatus"
                                            select
                                            inputRef={ref}
                                            error={Boolean(
                                                errors.plotmapStatus
                                            )}
                                            helperText={
                                                errors.plotmapStatus?.message
                                            }
                                        >
                                            {Object.entries(PLOTMAP_STATUS).map(
                                                ([key, { label }]) => (
                                                    <MenuItem
                                                        key={key}
                                                        value={Number(key)}
                                                    >
                                                        {label}
                                                    </MenuItem>
                                                )
                                            )}
                                        </TextField>
                                    )}
                                    rules={{
                                        required: INPUT_VALIDATION.REQUIRED,
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid size={6} />
                    {isDirty && (
                        <Grid size={12}>
                            <Stack
                                direction="row"
                                spacing={3}
                                justifyContent="flex-end"
                            >
                                <Button type="button" onClick={onCancel}>
                                    Cancel
                                </Button>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    loading={isSubmitting}
                                >
                                    Update
                                </LoadingButton>
                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </form>
        </>
    );
};
